import { flatten, omit } from 'lodash-es';
import { CostCenter } from '@portal/portal-core';

// Minimal shapes of the fields actually accessed on the super-client server response.
interface ProductAccessInfo {
  id: string;
  name: string;
  type: string;
  [key: string]: unknown;
}

export interface CostCenterInfo {
  id: string;
  name: string;
  type: string;
  owner: string;
  ownerEmail?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  ownerIsPhantom?: boolean;
  productAccess: ProductAccessInfo[];
}

interface ServerResponseData {
  costCenterAdmins?: Record<string, unknown>;
  costCenters?: Record<string, CostCenterInfo>;
}

export interface LicenseSelectorInfo {
  costCenter: Omit<CostCenterInfo, 'productAccess'>;
  productAccess: ProductAccessInfo;
  admin: unknown;
  allocatedSeats?: number;
  activeSharedSeats?: number | null;
}

// A single elasticsearch cost-center search hit, as returned (and seat-usage-enriched) by
// OpensearchCostCenters.searchCostCenters on the backend.
export interface LicenseSearchHit {
  _source: { json: CostCenterInfo };
}

export const formatServerResponse = (data: ServerResponseData = {}): LicenseSelectorInfo[] => {
  const adminDetails = data.costCenterAdmins || {};
  const licensesInfo = Object.values(data.costCenters || {}).map((costCenter) =>
    costCenter.productAccess.map((productAccess) => ({
      costCenter: omit(costCenter, ['productAccess']),
      productAccess,
      admin: adminDetails[costCenter.owner],
    })),
  );
  return flatten(licensesInfo);
};

// Mirrors formatServerResponse's flattening for elasticsearch search hits: one row per
// productAccess entry, same { costCenter, productAccess, admin } shape the license table
// columns already consume — so a table can render legacy-synced or search-derived rows
// interchangeably. `admin` is built from the owner fields the backend indexes/resolves
// (ownerEmail/ownerFirstName/ownerLastName/ownerIsPhantom) rather than from a separate
// costCenterAdmins lookup, since search hits don't have one. `phantom` matches the key name
// on the full user json formatServerResponse's `admin` already carries (see
// createPhantomUserAsync/user-manager.ts on the backend), so NameCell can read one field
// regardless of which path produced the row.
export const formatSearchResults = (hits: LicenseSearchHit[] = []): LicenseSelectorInfo[] => {
  const licensesInfo = hits.map((hit) => {
    const costCenter = hit._source.json;
    return costCenter.productAccess.map((productAccess) => {
      // The backend (OpensearchCostCenters.searchCostCenters -> enrichHitsWithSeatUsage) already
      // spreads allocatedSeats/activeSharedSeats onto each productAccess entry, but the table
      // columns read them off the ROW's top level (row.allocatedSeats), matching the shape the
      // legacy view's selectLicensesWithAllocatedSeats produces — lifted here so search rows
      // aren't silently missing values the backend already computed.
      const { allocatedSeats, activeSharedSeats } = productAccess as {
        allocatedSeats?: number;
        activeSharedSeats?: number | null;
      };
      return {
        costCenter: omit(costCenter, ['productAccess']),
        productAccess,
        admin: {
          primaryEmail: costCenter.ownerEmail,
          firstname: costCenter.ownerFirstName,
          lastname: costCenter.ownerLastName,
          phantom: costCenter.ownerIsPhantom ?? false,
        },
        allocatedSeats,
        activeSharedSeats,
      };
    });
  });
  return flatten(licensesInfo);
};

// helper function to find if there is matching costCenterName in the selector array of license information objects
export const individualMatch = (
  licenseSelectorInfo: LicenseSelectorInfo[] | undefined,
  name: string,
): string | undefined => {
  if (licenseSelectorInfo && licenseSelectorInfo[0]) {
    const findMatch = licenseSelectorInfo.find((obj) => obj.costCenter.name === name);
    if (findMatch && findMatch.costCenter.type === CostCenter.COST_CENTER_TYPES.individual)
      return findMatch.costCenter.id;
  }
  return undefined;
};
